import { IScope } from 'angular';
import { Module } from '../../ts/Module';
import { ISettingsCoordinatorCtrlScope, ISettingsFormScope, SettingsFormService } from '../directives/tbSettingsCoordinator';

/**
 * The props that are necessary to control the save/revert controller
 */
export interface BaseReactSettingsCtrlProps {
  setCancelCallback: (cancelFn: () => void) => void;
  setSaveCallback: (saveFn: () => ng.IPromise<any> | void) => void;
  setOpenNavigationDialogCallback: (callback: Function) => void;  // Popup that appears there's unsaved changes and a user tries to navigate away
  setIsFormDirty: (isFormModified: boolean) => void;
  setIsFormValid: (isValid: boolean) => void;
  setIsBusyCallback: (isBusyFn: () => boolean) => void;  // Angular expects a function callback here
  mergeIntoParentScope?: (values: unknown) => void;
}

export interface BaseReactSettingsCtrlScope<OtherProps> extends ISettingsCoordinatorCtrlScope {
  props: BaseReactSettingsCtrlProps & OtherProps;
}

export interface BaseReactSettingsCtrlSubScope<OtherProps> extends IScope {
  localForm: ISettingsFormScope;
  $parent: BaseReactSettingsCtrlScope<OtherProps>;
}

export class BaseReactSettingsCtrl<ChildCtrlProps> {

  private cancelFn: () => void = () => { };
  private isBusyFn: () => boolean = () => false;
  private saveFn: () => ng.IPromise<any>;
  private openNavigationDialog?: (successCallback: Function) => void;
  private formController: any;  // Just holds the $valid property used by the settingsCoordinator
  private isFormModified = false;

  constructor(
    private isolatedScope: BaseReactSettingsCtrlScope<ChildCtrlProps>,
    private formService: SettingsFormService,
    private formName: string
  ) {
    this.formController = { $valid: true };
    // Creates a new scope that inherits the original scope as the parent of the newly created scope
    // This is to support a locally-scoped localForm. Without this, another angular localForm on the
    // same scope can be unintentionally overwritten.
    this.setupAngularForm();
    this.setupReactProps();
    this.setupNavigationConfirmationDialog();
  }

  /**
   * Registers this controller to the SharedSettings forms
   */
  private setupAngularForm() {
    // The scope is the new scope that has a local-level attribute 'localForm'.
    // This is necessary so we don't overwrite the localForm of the parent.
    this.isolatedScope.localForm = {
      formController: this.formController,
      cancel: () => this.cancelFn(),
      isBusy: () => this.isBusyFn(),
      isFormUnchanged: () => !this.isFormModified,
      save: () => this.saveFn(),
    };

    this.formService.registerFormStateByName((this.isolatedScope as any).localForm, this.formName);
  }

  private setupReactProps() {
    // The React-Angular adapter is still using the original scope that was passed in, so use the parentScope.props
    (this.isolatedScope.props as BaseReactSettingsCtrlProps) = {
      setSaveCallback: this.setSaveCallback.bind(this),
      setCancelCallback: this.setCancelCallback.bind(this),
      setIsBusyCallback: this.setIsBusyCallback.bind(this),
      setIsFormDirty: this.setIsFormModifiedCallback.bind(this),
      setIsFormValid: this.setIsFormValidCallback.bind(this),
      setOpenNavigationDialogCallback: this.setOpenNavigationDialog.bind(this),
    };
  }

  /**
   * Displays a popup when the user tries to navigate away from the page before saving changes
   */
  private setupNavigationConfirmationDialog() {
    // Handles the close tab / reload use-cases. Shows the built-in browser popup.
    window.onbeforeunload = () => {
      return (this.isFormModified && this.openNavigationDialog) ? '' : undefined;  // non-undefined value opens up
    };
    // Handles navigating to another page/history with Angular router. Shows our custom popup
    const deregisterListener = this.isolatedScope.$on('$locationChangeStart', (event: any, newUrl: any) => {
      if (this.isFormModified && this.openNavigationDialog) {
        event.preventDefault();
        this.openNavigationDialog(() => {
          deregisterListener();
          window.location = newUrl;
        });
      }
    });
  }

  public setCancelCallback(cancelFn: () => void) {
    this.cancelFn = cancelFn;
  }

  public setIsBusyCallback(isBusyFn: () => boolean) {
    this.isBusyFn = isBusyFn;
  }

  public setSaveCallback(saveFn: () => ng.IPromise<any>) {
    this.saveFn = saveFn;
  }

  /**
   * triggerAngularRefresh: For certain functions like save/cancel, the Angular Settings Ctrl
   * already calls $scope.digest, so calling $apply would throw an error. It's called manually
   * here for UI changes that Angular is not aware of, eg all React component changes.
   */
  public setIsFormModifiedCallback(isFormModified: boolean) {
    this.isFormModified = isFormModified;
    this.isolatedScope.$evalAsync(angular.noop);
  }

  public setIsFormValidCallback(isFormValid: boolean) {
    this.formController.$valid = isFormValid;
  }

  /**
   * The dialog is a shared-widget. Since it's React, delegate the dialog handling
   * to the Customization Settings component.
   */
  public setOpenNavigationDialog(fn: () => void) {
    this.openNavigationDialog = fn;
  }
}
